import { defineComponent as _defineComponent } from 'vue'
import { computed, defineAsyncComponent, ref } from 'vue';
import { useMounted } from '@vueuse/core';

import { ClipProvider } from '@/@types/graphql-types';
import type { Clip, ObjectType } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { VideoPlayersTrackingIds } from '@/constants/video-players-tracking-ids';
import PictureCompBackdrop from '@/pages/titleDetailV1/v1/hero/PictureCompBackdrop.vue';
import { useBackgroundVideoTracking } from '../tracking';

interface PlayerExpose {
	mute(): void;
	unmute(): void;
	play(): void;
	pause(): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BackgroundVideoPlayer',
  props: {
    clip: null,
    trackingDetails: null,
    backdropImageUrl: null,
    additionalContexts: null
  },
  emits: ["videoReady"],
  setup(__props: any, { expose, emit }: { emit: ({
	(e: 'videoReady'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	clip: Clip;
	trackingDetails: {
		placement: string;
		titleObjectType: ObjectType;
	};
	backdropImageUrl?: string;
	additionalContexts?: SnowplowContext[];
};

const DailyMotionBackgroundPlayer = defineAsyncComponent(() => import('./DailyMotionBackgroundPlayer.vue'));
const YouTubeBackgroundPlayer = defineAsyncComponent(() => import('./YouTubeBackgroundPlayer.vue'));





const isMounted = useMounted();
const isVideoReady = ref(false);
const playerRef = ref<PlayerExpose | null>(null);

const activePlayerComponent = computed(() => {
	if (props.clip.provider === ClipProvider.Dailymotion) return DailyMotionBackgroundPlayer;
	if (props.clip.provider === ClipProvider.Youtube) return YouTubeBackgroundPlayer;
	return null;
});

const externalId = computed(() => {
	if (props.clip.provider === ClipProvider.Dailymotion) {
		try {
			return new URL(props.clip.sourceUrl).searchParams.get('video') ?? '';
		} catch {
			return '';
		}
	}
	return props.clip.externalId;
});

const shouldRenderPlayer = computed(() => isMounted.value && !!externalId.value);

const { trackBackgroundVideo } = useBackgroundVideoTracking({
	value: VideoPlayersTrackingIds[props.clip.provider],
	action: props.trackingDetails.titleObjectType?.toLowerCase(),
	label: props.trackingDetails.placement,
});

function onVideoReady() {
	isVideoReady.value = true;
	trackBackgroundVideo('playing');
	emit('videoReady');
}

function onVideoBuffering() {
	trackBackgroundVideo('buffering');
}

function onVideoErrored() {
	trackBackgroundVideo('errored');
}

function mute() {
	playerRef.value?.mute();
}

function unmute() {
	playerRef.value?.unmute();
}

function play() {
	playerRef.value?.play();
}

function pause() {
	playerRef.value?.pause();
}

expose({ mute, unmute, play, pause });

return { __sfc: true,DailyMotionBackgroundPlayer, YouTubeBackgroundPlayer, props, emit, isMounted, isVideoReady, playerRef, activePlayerComponent, externalId, shouldRenderPlayer, trackBackgroundVideo, onVideoReady, onVideoBuffering, onVideoErrored, mute, unmute, play, pause, PictureCompBackdrop }
}

})